import React, { useState, useEffect, FormEvent } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Gift, DollarSign, Heart, AlertCircle } from 'react-feather';
import VisitorLayout from '../../components/layouts/VisitorLayout';
import Loader from '../../components/common/Loader';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import Textarea from '../../components/common/Textarea';
import Alert from '../../components/common/Alert';
import Modal from '../../components/common/Modal';
import CoupleService from '../../services/couple.service';
import RegistryService from '../../services/registry.service';
import PaymentService from '../../services/payment.service';
import UploadService from '../../services/upload.service';
import { toast } from 'react-toastify';
import {
  formatPriceSimple,
  getAmountInMinorUnit,
} from '../../utils/currency';
import { Couple, Gift as GiftItem, GiftStatus } from '../../types';

const Registry: React.FC = () => {
  const { customUrl } = useParams<{ customUrl: string }>();
  const [couple, setCouple] = useState<Couple | null>(null);
  const [gifts, setGifts] = useState<GiftItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Track which images failed to load
  const [failedImages, setFailedImages] = useState<Record<string, boolean>>({});

  // Payment Modal States
  const [isGiftModalOpen, setIsGiftModalOpen] = useState<boolean>(false);
  const [isFundModalOpen, setIsFundModalOpen] = useState<boolean>(false);
  const [selectedGift, setSelectedGift] = useState<GiftItem | null>(null);

  // Form States for Contribution Modal
  const [contributorName, setContributorName] = useState<string>('');
  const [contributorEmail, setContributorEmail] = useState<string>('');
  const [message, setMessage] = useState<string>('');
  const [amount, setAmount] = useState<number>(1000);
  // Fixed currency to NGN only
  const currency = 'NGN';
  const [formLoading, setFormLoading] = useState<boolean>(false);

  // Gift reservation states
  const [isReservingGift, setIsReservingGift] = useState<boolean>(false);

  useEffect(() => {
    const fetchData = async () => {
      if (!customUrl) {
        setLoading(false);
        setError('No couple information provided');
        return;
      }

      try {
        setLoading(true);
        const coupleData = await CoupleService.getCoupleByCustomUrl(customUrl);
        setCouple(coupleData);

        if (coupleData) {
          const items = await RegistryService.getCoupleGifts(coupleData._id);
          setGifts(items);
        }

        setError(null);
        setFailedImages({});
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load registry';
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [customUrl]);

  const getDisplayUrl = (url: string): string => {
    if (!url) return '';
    return UploadService.getOptimizedImageUrl(url);
  };

  const handleImageError = (giftId: string) => {
    setFailedImages(prev => ({
      ...prev,
      [giftId]: true,
    }));
  };

  const openGiftModal = (gift: GiftItem) => {
    setSelectedGift(gift);
    setIsGiftModalOpen(true);
  };

  const handleReserveGift = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!couple || !selectedGift) return;

    setIsReservingGift(true);

    try {
      const reservationData = {
        contributorName: contributorName,
        contributorEmail: contributorEmail,
        note: message,
      };

      const updatedGift = await RegistryService.reserveGift(selectedGift._id, reservationData);

      setGifts(gifts.map(gift =>
        gift._id === updatedGift._id ? updatedGift : gift
      ));

      setIsGiftModalOpen(false);
      resetForm();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to reserve gift';
      setError(errorMessage);
    } finally {
      setIsReservingGift(false);
    }
  };

  const handleContributeToFund = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!couple) return;

    if (!contributorName.trim()) {
      toast.error('Please enter your name.');
      return;
    }

    if (amount < 100) {
      toast.error('Amount must be at least 100 Naira.');
      return;
    }

    setFormLoading(true);

    try {
      const amountInMinorUnit = getAmountInMinorUnit(amount, currency);

      const transactionData = {
        amount: amountInMinorUnit,
        email: contributorEmail || `guest-${Date.now()}@example.com`,
        currency: currency,
        metadata: {
          type: 'contribution',
          contributionType: 'wedding_fund',
          couple: couple._id,
          contributorName,
          contributorEmail: contributorEmail || undefined,
          message: message || undefined,
          currency: currency,
        },
      };

      const paymentResponse = await PaymentService.publicInitializeTransaction(transactionData);

      if (paymentResponse?.data?.authorization_url) {
        localStorage.setItem('contributionCoupleUrl', customUrl || '');
        localStorage.setItem('isContribution', 'true');
        localStorage.setItem('paystack_reference', paymentResponse.data.reference);

        try {
          localStorage.setItem(
            'contributionDetails',
            JSON.stringify({
              amount: amount,
              currency: currency,
              contributorName: contributorName,
              type: 'Wedding Fund',
            })
          );
        } catch (e) {
          console.warn('Could not store contribution details:', e);
        }

        const redirectUrl = new URL(paymentResponse.data.authorization_url);
        redirectUrl.searchParams.append('isContribution', 'true');
        window.location.href = redirectUrl.toString();
      } else {
        throw new Error('Could not initialize payment. Invalid response from server.');
      }
    } catch (err: any) {
      console.error('Payment initialization error:', err);
      toast.error(err.response?.data?.message || 'Failed to process your contribution. Please try again.');
    } finally {
      setFormLoading(false);
    }
  };

  const resetForm = () => {
    setContributorName('');
    setContributorEmail('');
    setMessage('');
    setAmount(1000);
    setSelectedGift(null);
  };

  const getAvailableGifts = () => {
    return gifts.filter(gift => gift.status === 'available');
  };

  const getReservedGifts = () => {
    return gifts.filter(gift => gift.status === 'reserved');
  };

  const getPurchasedGifts = () => {
    return gifts.filter(gift => gift.status === 'purchased');
  };

  if (loading) {
    return (
      <VisitorLayout>
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500"></div>
          <p className="ml-3 text-gray-600">Loading registry...</p>
        </div>
      </VisitorLayout>
    );
  }

  if (error || !couple) {
    return (
      <VisitorLayout>
        <div className="py-10 text-center">
          <div className="mb-6">
            <span className="inline-block bg-red-100 p-3 rounded-full">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-8 w-8 text-red-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </span>
          </div>
          <h1 className="text-2xl font-bold text-gray-800 mb-4">Registry Not Found</h1>
          <p className="text-gray-600 mb-8">{error || "We couldn't find the registry you're looking for."}</p>
          <Link
            to="/"
            className="bg-pink-500 text-white px-6 py-3 rounded-full font-medium hover:bg-pink-600 transition-colors"
          >
            Return to Homepage
          </Link>
        </div>
      </VisitorLayout>
    );
  }

  const availableGifts = getAvailableGifts();
  const reservedGifts = getReservedGifts();
  const purchasedGifts = getPurchasedGifts();

  return (
    <VisitorLayout couple={couple}>
      <div className="bg-white">
        <div className="max-w-7xl mx-auto py-16 px-4 sm:py-24 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">Wedding Registry</h2>
            <p className="mt-3 max-w-2xl mx-auto text-lg text-gray-500 sm:mt-4">
              Thank you for your love and support as we begin our new life together!
            </p>
          </div>

          {error && (
            <Alert
              type="error"
              message={error}
              dismissible
              onDismiss={() => setError(null)}
              className="mt-6 max-w-2xl mx-auto"
            />
          )}

          {/* Wedding Fund Section */}
          <div className="mt-12 bg-pink-50 rounded-lg overflow-hidden shadow">
            <div className="px-4 py-5 sm:p-6">
              <div className="sm:flex sm:items-center sm:justify-between">
                <div>
                  <h3 className="text-lg leading-6 font-medium text-gray-900 flex items-center">
                    <Heart className="mr-2 h-5 w-5 text-pink-500" />
                    Wedding Fund
                  </h3>
                  <div className="mt-2 max-w-xl text-sm text-gray-500">
                    <p>
                      Contribute to our wedding fund to help us start our married life together.
                    </p>
                    {couple.fundingGoal ? (
                      <div className="mt-3">
                        <p className="font-medium">Our goal: ₦{couple.fundingGoal.toLocaleString()}</p>
                      </div>
                    ) : null}
                  </div>
                </div>
                <div className="mt-5 sm:mt-0 sm:ml-6 sm:flex-shrink-0">
                  <Button
                    onClick={() => {
                      resetForm();
                      setIsFundModalOpen(true);
                    }}
                    leftIcon={<DollarSign className="h-4 w-4" />}
                  >
                    Contribute
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Registry Items Section */}
          <div className="mt-12">
            <h3 className="text-xl font-semibold text-gray-900 mb-6">Gift Registry</h3>

            {gifts.length === 0 ? (
              <div className="text-center py-10 bg-gray-50 rounded-lg">
                <Gift className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No items in registry</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Check back later for gift registry items.
                </p>
              </div>
            ) : (
              <div className="space-y-8">
                {/* Available Items */}
                {availableGifts.length > 0 && (
                  <div>
                    <h4 className="text-lg font-medium text-gray-700 mb-4">Available Gifts</h4>
                    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3">
                      {availableGifts.map((gift) => (
                        <div key={gift._id} className="border rounded-lg overflow-hidden">
                          <div className="h-48 bg-gray-200">
                            {gift.image ? (
                              failedImages[gift._id] ? (
                                <div className="h-full w-full flex items-center justify-center">
                                  <div className="text-center p-4">
                                    <AlertCircle className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                                    <p className="text-sm text-gray-500">Image could not be loaded</p>
                                  </div>
                                </div>
                              ) : (
                                <img
                                  src={getDisplayUrl(gift.image)}
                                  alt={gift.name}
                                  className="h-full w-full object-cover"
                                  onError={() => handleImageError(gift._id)}
                                  loading="lazy"
                                />
                              )
                            ) : (
                              <div className="h-full w-full flex items-center justify-center">
                                <Gift className="h-12 w-12 text-gray-400" />
                              </div>
                            )}
                          </div>
                          <div className="p-4">
                            <h4 className="font-medium text-gray-900">{gift.name}</h4>
                            {gift.description && (
                              <p className="mt-1 text-sm text-gray-500">{gift.description}</p>
                            )}
                            <div className="mt-3 flex justify-between items-center">
                              <p className="font-medium text-gray-900">₦{(gift.price || 0).toLocaleString()}</p>
                              <Button
                                size="sm"
                                onClick={() => openGiftModal(gift)}
                              >
                                Gift This
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Reserved Items */}
                {reservedGifts.length > 0 && (
                  <div>
                    <h4 className="text-lg font-medium text-gray-700 mb-4">Reserved Gifts</h4>
                    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3">
                      {reservedGifts.map((gift) => (
                        <div key={gift._id} className="border rounded-lg overflow-hidden bg-gray-50">
                          <div className="h-48 bg-gray-200">
                            {gift.image ? (
                              failedImages[gift._id] ? (
                                <div className="h-full w-full flex items-center justify-center">
                                  <div className="text-center p-4">
                                    <AlertCircle className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                                    <p className="text-sm text-gray-500">Image could not be loaded</p>
                                  </div>
                                </div>
                              ) : (
                                <img
                                  src={getDisplayUrl(gift.image)}
                                  alt={gift.name}
                                  className="h-full w-full object-cover"
                                  onError={() => handleImageError(gift._id)}
                                  loading="lazy"
                                />
                              )
                            ) : (
                              <div className="h-full w-full flex items-center justify-center">
                                <Gift className="h-12 w-12 text-gray-400" />
                              </div>
                            )}
                          </div>
                          <div className="p-4">
                            <div className="flex justify-between items-start">
                              <h4 className="font-medium text-gray-900">{gift.name}</h4>
                              <span className="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full">
                                Reserved
                              </span>
                            </div>
                            {gift.description && (
                              <p className="mt-1 text-sm text-gray-500">{gift.description}</p>
                            )}
                            <div className="mt-3">
                              <p className="font-medium text-gray-900">₦{(gift.price || 0).toLocaleString()}</p>
                            </div>
                            <p className="mt-1 text-sm text-gray-500">
                              Reserved
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Purchased Items */}
                {purchasedGifts.length > 0 && (
                  <div>
                    <h4 className="text-lg font-medium text-gray-700 mb-4">Purchased Gifts</h4>
                    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3">
                      {purchasedGifts.map((gift) => (
                        <div key={gift._id} className="border rounded-lg overflow-hidden bg-gray-50">
                          <div className="h-48 bg-gray-200">
                            {gift.image ? (
                              failedImages[gift._id] ? (
                                <div className="h-full w-full flex items-center justify-center">
                                  <div className="text-center p-4">
                                    <AlertCircle className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                                    <p className="text-sm text-gray-500">Image could not be loaded</p>
                                  </div>
                                </div>
                              ) : (
                                <img
                                  src={getDisplayUrl(gift.image)}
                                  alt={gift.name}
                                  className="h-full w-full object-cover"
                                  onError={() => handleImageError(gift._id)}
                                  loading="lazy"
                                />
                              )
                            ) : (
                              <div className="h-full w-full flex items-center justify-center">
                                <Gift className="h-12 w-12 text-gray-400" />
                              </div>
                            )}
                          </div>
                          <div className="p-4">
                            <div className="flex justify-between items-start">
                              <h4 className="font-medium text-gray-900">{gift.name}</h4>
                              <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                                Purchased
                              </span>
                            </div>
                            {gift.description && (
                              <p className="mt-1 text-sm text-gray-500">{gift.description}</p>
                            )}
                            <div className="mt-3">
                              <p className="font-medium text-gray-900">₦{(gift.price || 0).toLocaleString()}</p>
                            </div>
                            <p className="mt-1 text-sm text-gray-500">
                              Purchased
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Gift Modal */}
      <Modal
        open={isGiftModalOpen}
        onClose={() => setIsGiftModalOpen(false)}
        title="Gift an Item"
      >
        {selectedGift && (
          <div className="space-y-4">
            <div className="flex items-center space-x-4">
              {selectedGift.image ? (
                failedImages[selectedGift._id] ? (
                  <div className="h-16 w-16 bg-gray-100 flex items-center justify-center rounded-md">
                    <AlertCircle className="h-6 w-6 text-gray-400" />
                  </div>
                ) : (
                  <div className="h-16 w-16 bg-gray-200 rounded-md">
                    <img
                      src={getDisplayUrl(selectedGift.image)}
                      alt={selectedGift.name}
                      className="h-full w-full object-cover rounded-md"
                      onError={() => handleImageError(selectedGift._id)}
                    />
                  </div>
                )
              ) : (
                <div className="h-16 w-16 bg-gray-200 flex items-center justify-center rounded-md">
                  <Gift className="h-8 w-8 text-gray-400" />
                </div>
              )}
              <div>
                <h3 className="text-lg font-medium text-gray-900">{selectedGift.name}</h3>
                <p className="text-sm text-gray-500">₦{(selectedGift.price || 0).toLocaleString()}</p>
              </div>
            </div>

            <div id="gift-option-selection" className="p-4 bg-gray-50 rounded-md">
              <div className="flex space-x-2">
                <button
                  className="flex-1 py-2 px-4 border rounded-md bg-white shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500"
                  onClick={() => setIsGiftModalOpen(false)}
                >
                  Cancel
                </button>
                <Link
                  to={`/gift-payment/${customUrl}?giftId=${selectedGift._id}&item=${encodeURIComponent(selectedGift.name)}`}
                  className="flex-1 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-pink-600 hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500 text-center"
                >
                  Purchase Now
                </Link>
              </div>
              <div className="mt-2 text-center">
                <button
                  className="text-sm text-pink-600 hover:text-pink-500"
                  onClick={() => {
                    document.getElementById('gift-option-selection')?.classList.add('hidden');
                    document.getElementById('gift-reserve-form')?.classList.remove('hidden');
                  }}
                >
                  Reserve for later
                </button>
              </div>
              <p className="mt-2 text-xs text-gray-500 text-center">
                By purchasing, you'll pay now. By reserving, you're indicating your intention to buy this gift elsewhere.
              </p>
            </div>

            {/* Reserve Form */}
            <div id="gift-reserve-form" className="hidden">
              <form onSubmit={handleReserveGift} className="space-y-4">
                <Input
                  id="reserve-name"
                  label="Your Name"
                  required
                  value={contributorName}
                  onChange={(e) => setContributorName(e.target.value)}
                  placeholder="Enter your name"
                />
                <Input
                  id="reserve-email"
                  label="Your Email"
                  type="email"
                  required
                  value={contributorEmail}
                  onChange={(e) => setContributorEmail(e.target.value)}
                  placeholder="Enter your email"
                />
                <Textarea
                  id="reserve-message"
                  label="Message (optional)"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Add a message for the couple"
                />
                <div className="flex justify-end space-x-3">
                  <Button
                    variant="outline"
                    onClick={() => setIsGiftModalOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    loading={isReservingGift}
                    disabled={isReservingGift || !contributorName || !contributorEmail}
                  >
                    Reserve Item
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}
      </Modal>

      {/* Fund Contribution Modal */}
      <Modal
        open={isFundModalOpen}
        onClose={() => setIsFundModalOpen(false)}
        title="Contribute to Wedding Fund"
      >
        <form onSubmit={handleContributeToFund} className="space-y-4">
          <Input
            id="fund-name"
            label="Your Name *"
            required
            value={contributorName}
            onChange={(e) => setContributorName(e.target.value)}
            placeholder="Enter your name"
          />
          <Input
            id="fund-email"
            label="Your Email (Optional)"
            type="email"
            value={contributorEmail}
            onChange={(e) => setContributorEmail(e.target.value)}
            placeholder="Enter your email"
          />
          <div className="mb-6">
            <div className="flex items-center">
              <div className="bg-gray-100 text-gray-800 px-4 py-2 rounded-lg font-medium border border-gray-200">
                Currency: Nigerian Naira (₦)
              </div>
            </div>
          </div>
          <div className="mb-6">
            <label htmlFor="fund-amount" className="block text-gray-700 font-medium mb-2">
              Amount (₦) *
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <span className="text-gray-700 font-medium">₦</span>
              </div>
              <input
                type="number"
                id="fund-amount"
                className="w-full pl-8 pr-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500 !text-gray-900"
                placeholder="Enter amount"
                min="100"
                value={amount}
                onChange={(e) => setAmount(Number(e.target.value))}
                required
              />
            </div>
            <p className="text-gray-500 text-sm mt-1">Minimum amount: ₦100</p>
          </div>
          <Textarea
            id="fund-message"
            label="Message (optional)"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Add a message for the couple"
          />
          <div className="text-center mt-8">
            <div className="mb-4">
              <p className="text-gray-700 font-bold text-2xl">₦{amount.toLocaleString()}</p>
            </div>
            <Button
              type="submit"
              loading={formLoading}
              disabled={formLoading || !contributorName || !amount}
              className={`w-full py-4 px-6 rounded-full text-white font-medium focus:outline-none transition-colors ${
                formLoading ? 'bg-pink-400 cursor-not-allowed' : 'bg-pink-600 hover:bg-pink-700'
              }`}
            >
              {formLoading ? (
                <span className="flex items-center justify-center">
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Processing...
                </span>
              ) : (
                <span className="flex items-center justify-center">
                  <DollarSign className="h-5 w-5 mr-2" />
                  Send Contribution
                </span>
              )}
            </Button>
          </div>
        </form>
      </Modal>
    </VisitorLayout>
  );
};

export default Registry;